import React, { useCallback, useMemo, useState } from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import useInhouseEmails from "hooks/Inhouse emails/useInhouseEmails";

const splitEmails = (value) => {
  if (!value) return [];
  return String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const createCorrelationId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  const template = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx";
  return template.replace(/[xy]/g, (char) => {
    const rand = (Math.random() * 16) | 0;
    const value = char === "x" ? rand : (rand & 0x3) | 0x8;
    return value.toString(16);
  });
};

export default function ComposeInhouseEmailPage() {
  const { sendEmail } = useInhouseEmails();
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const canSubmit = useMemo(
    () => splitEmails(to).length > 0 && subject.trim(),
    [to, subject],
  );

  const handleSendEmail = useCallback(
    async (payload) => {
      if (sending) return;
      setSending(true);
      try {
        await sendEmail(payload, { correlationId: createCorrelationId() });
        toast.success("Email sent successfully", { theme: "colored" });
        setTo("");
        setCc("");
        setBcc("");
        setSubject("");
        setBody("");
      } catch (err) {
        toast.error(err?.message || "Failed to send email", {
          theme: "colored",
        });
      } finally {
        setSending(false);
      }
    },
    [sendEmail, sending],
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit || sending) return;

    const payload = {
      to: splitEmails(to),
      cc: splitEmails(cc),
      bcc: splitEmails(bcc),
      subject: subject.trim(),
      title: subject.trim(),
      body,
      content: body,
    };

    await handleSendEmail(payload);
  };

  return (
    <>
      <WidgetSectionTitle
        icon="paper-plane"
        title="Compose Inhouse Email"
        subtitle="Send a new inhouse email to recipients."
        transform="shrink-2"
        className="mb-3 mt-3"
      />

      <div className="p-3 bg-light rounded">
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-2">
            <Form.Label className="small text-muted">To</Form.Label>
            <Form.Control
              type="text"
              placeholder="recipient@example.com, another@example.com"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              required
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label className="small text-muted">CC</Form.Label>
            <Form.Control
              type="text"
              placeholder="optional"
              value={cc}
              onChange={(e) => setCc(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label className="small text-muted">BCC</Form.Label>
            <Form.Control
              type="text"
              placeholder="optional"
              value={bcc}
              onChange={(e) => setBcc(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label className="small text-muted">Subject</Form.Label>
            <Form.Control
              type="text"
              placeholder="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="small text-muted">Message</Form.Label>
            <Form.Control
              as="textarea"
              rows={8}
              placeholder="Write your message..."
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
          </Form.Group>
          <Button variant="success" size="sm" type="submit" disabled={!canSubmit || sending}>
            {sending ? <Spinner animation="border" size="sm" /> : "Send"}
          </Button>
        </Form>
      </div>
    </>
  );
}
